import { useEffect, useRef, useState } from 'react';

import { BugReportDialog } from './BugReportDialog';
import { bugReportStore } from './bugReportStore';
import { captureScreenshot } from './utils';

/**
 * Hosts the custom bug report dialog. Mounted only when
 * `useBugReportWidget()` returns `'custom'` — the parent owns that decision so
 * this component can assume it should render unconditionally.
 *
 * Renders no trigger. The dialog opens only on an `openRequested` event.
 *
 * State ownership:
 * - isDialogOpen / isMinimized / screenshot are lifted here so the screenshot
 *   survives a minimize → restore cycle without being lost when the dialog
 *   unmounts.
 */
export function BugReportButton() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [screenshot, setScreenshot] = useState<File | undefined>();

  const handleTriggerClick = async () => {
    if (isMinimized) {
      // Restore: re-open with the preserved screenshot and draft
      setIsMinimized(false);
      setIsDialogOpen(true);
      return;
    }

    // Open immediately so the dialog appears without delay, then capture the
    // screenshot in the background. MuiDialog-root / MuiModal-root are already
    // excluded by shouldIgnoreElement so the open dialog won't appear in the capture.
    setIsDialogOpen(true);
    try {
      const captured = await captureScreenshot();
      setScreenshot(captured);
    } catch {
      // Silently fall back — the user can capture manually inside the dialog.
    }
  };

  // Subscribe once to `openRequested` and invoke the latest handler.
  const handleTriggerClickRef = useRef(handleTriggerClick);

  useEffect(() => {
    handleTriggerClickRef.current = handleTriggerClick;
  });

  useEffect(() => {
    const subscription = bugReportStore.on('openRequested', () => {
      void handleTriggerClickRef.current();
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleClose = () => {
    setIsDialogOpen(false);
    setIsMinimized(false);
  };

  const handleMinimize = () => {
    setIsDialogOpen(false);
    setIsMinimized(true);
  };

  return (
    <BugReportDialog
      isOpen={isDialogOpen}
      screenshot={screenshot}
      setScreenshot={setScreenshot}
      onClose={handleClose}
      onMinimize={handleMinimize}
    />
  );
}
